// What a weapon row's usages say it can do: the modes it offers, the damage types it deals,
// and the line each usage prints on a shop row. Every value here is GCS's bake, read as it
// was written.

import type { BakedRow } from "./catalogue";

export const MODES = ["melee", "ranged"] as const;
export type Mode = (typeof MODES)[number];

export const DAMAGES = ["crushing", "cutting", "impaling", "piercing"] as const;
export type Damage = (typeof DAMAGES)[number];

/** The closed set of GURPS damage-type codes. `pi-` through `pi++` differ only in the
 *  wound multiplier, so they share a chip, and a value outside this set draws none. */
const CODES: Record<string, Damage> = {
  cr: "crushing",
  cut: "cutting",
  imp: "impaling",
  "pi-": "piercing",
  pi: "piercing",
  "pi+": "piercing",
  "pi++": "piercing",
};

const RANGED = ["accuracy", "range", "rate_of_fire", "bulk", "recoil", "shots"];
const MELEE = ["reach", "parry", "block"];

interface Usage {
  usage?: unknown;
  reach?: unknown;
  damage?: { type?: unknown };
  calc?: {
    level?: unknown;
    damage?: unknown;
    parry?: unknown;
    block?: unknown;
    range?: unknown;
    accuracy?: unknown;
  };
  [field: string]: unknown;
}

export function usagesOf(row: BakedRow): Usage[] {
  const weapons = row["weapons"];
  return Array.isArray(weapons) ? (weapons as Usage[]) : [];
}

function modeOf(usage: Usage): Mode | null {
  if (RANGED.some((field) => usage[field] !== undefined)) return "ranged";
  if (MELEE.some((field) => usage[field] !== undefined)) return "melee";
  return null;
}

/** A hatchet is swung and thrown, so it answers to both. Narrowing this to one mode per row
 *  would hide a thrown blade from Ranged. */
export function modesOf(row: BakedRow): Set<Mode> {
  const found = new Set<Mode>();
  for (const usage of usagesOf(row)) {
    const mode = modeOf(usage);
    if (mode !== null) found.add(mode);
  }
  return found;
}

/** `imp / thr-1 cut` is two types in one string, so the written value is read as tokens
 *  against the closed set. Reading it any other way needs a parser for the whole
 *  expression. */
export function damagesOf(row: BakedRow): Set<Damage> {
  const found = new Set<Damage>();
  for (const usage of usagesOf(row)) {
    const written = usage.damage?.type;
    if (typeof written !== "string") continue;
    for (const token of written.split(/[\s/]+/)) {
      const kind = CODES[token];
      if (kind !== undefined) found.add(kind);
    }
  }
  return found;
}

/** One usage as a shop row prints it. A usage GCS resolved no level for carries no stats,
 *  because there is no attack to state. */
export interface UsageLine {
  name: string;
  level: number | null;
  stats: [string, string][];
}

export function usageLines(row: BakedRow): UsageLine[] {
  return usagesOf(row).map((usage) => {
    const calc = usage.calc ?? {};
    const roll = calc.level;
    const level = typeof roll === "number" ? roll : null;
    return {
      name: typeof usage.usage === "string" ? usage.usage : "",
      level,
      stats: level === null ? [] : readings(usage, calc),
    };
  });
}

function readings(usage: Usage, calc: NonNullable<Usage["calc"]>): [string, string][] {
  const out: [string, string][] = [];
  const add = (label: string, value: unknown): void => {
    if (typeof value === "string" && value !== "") out.push([label, value]);
  };
  add("", calc.damage);
  add("Range", calc.range);
  add("Reach", usage.reach);
  add("Acc", calc.accuracy);
  add("Parry", calc.parry);
  add("Block", calc.block);
  return out;
}

/**
 * The usage this character does best with, which is the one a row states. Rank is the
 * skill level GCS resolved and then GCS's document order, and an unskilled usage never
 * outranks a skilled one. Damage does not rank: weighing 2d+1 cut against 1d+3 imp is a
 * judgement GURPS states nowhere in the data.
 */
export function bestUsage(lines: UsageLine[]): UsageLine | null {
  let best: UsageLine | null = null;
  for (const line of lines) {
    if (best === null) {
      best = line;
      continue;
    }
    if (line.level === null) continue;
    if (best.level === null || line.level > best.level) best = line;
  }
  return best;
}

export function usageText(line: UsageLine): string {
  const parts = line.level === null ? ["no skill"] : [String(line.level)];
  for (const [label, value] of line.stats) {
    parts.push(label === "" ? value : `${label} ${value}`);
  }
  return line.name === "" ? parts.join(", ") : `${line.name} ${parts.join(", ")}`;
}
